import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import DataObjectIcon from "@mui/icons-material/DataObject";
import TableViewIcon from "@mui/icons-material/TableView";
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";

import { updateGeneralSetting } from "store/reducers/inputReducer.js";
import { selectDownloadType } from "store/reducers/selectors/settingsSelectors.js";
import { selectRememberedSwitch, setRememberedSwitch } from "store/reducers/uiReducer.js";

const FORMATS = [
  {
    value: "json",
    label: ".json",
    icon: <DataObjectIcon fontSize="small" />,
  },
  {
    value: "csv",
    label: ".csv",
    icon: <TableViewIcon fontSize="small" />,
  },
  {
    value: "xlsx",
    label: ".xlsx",
    icon: <TableViewIcon fontSize="small" />,
  },
];

// Sub-component removed as logic is now integrated into main component

const ExportFormatDialog = memo((props) => {
  const { onClose, open, handleCancel } = props;

  const dispatch = useDispatch();
  const downloadType = useSelector(selectDownloadType);
  const downloadTypeKeepSelection = useSelector(selectRememberedSwitch('rememberDownloadFormat'));

  const [keepSelection, setKeepSelection] = useState(Boolean(downloadTypeKeepSelection));
  const keepSelectionRef = useRef(Boolean(downloadTypeKeepSelection));

  useEffect(() => {
    const val = Boolean(downloadTypeKeepSelection);
    setKeepSelection(val);
    keepSelectionRef.current = val;
  }, [downloadTypeKeepSelection]);

  const handleToggleChange = useCallback((event) => {
    const checked = event.target.checked;
    setKeepSelection(checked);
    keepSelectionRef.current = checked;
  }, []);

  const persistSelectionPreference = useCallback(() => {
    dispatch(
      setRememberedSwitch({
        key: "rememberDownloadFormat",
        value: keepSelectionRef.current,
      })
    );
  }, [dispatch]);

  const closeAndDownload = useCallback(() => {
    persistSelectionPreference();
    if (downloadType) {
      onClose();
    }
  }, [downloadType, onClose, persistSelectionPreference]);

  const handleDialogCancel = useCallback(() => {
    persistSelectionPreference();
    handleCancel();
  }, [handleCancel, persistSelectionPreference]);

  const handleFormatChange = useCallback((_event, value) => {
    if (!value) return;
    dispatch(updateGeneralSetting({ settingName: "downloadType", value }));
  }, [dispatch]);

  const formatSelector = useMemo(() => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mb: 1.5 }}>
        Select export format
      </Typography>

      <ToggleButtonGroup
        value={downloadType || null}
        exclusive
        onChange={handleFormatChange}
        fullWidth
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          flexWrap: "wrap",
          "& .MuiToggleButton-root": {
            flex: "1 1 0",
            minWidth: "80px",
            borderRadius: "8px !important",
            border: "1px solid !important",
            borderColor: "divider !important",
            textTransform: "none",
            px: 2,
            py: 1.5,
            transition: "all 0.15s",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
          },
          "& .MuiToggleButton-root.Mui-selected": {
            backgroundColor: "rgba(0, 146, 139, 0.10) !important",
            borderColor: "primary.main !important",
            color: "primary.main !important",
          },
        }}
      >
        {FORMATS.map((format) => (
          <ToggleButton key={format.value} value={format.value}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 146, 139, 0.08)",
                color: "primary.main",
              }}
            >
              {format.icon}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 750 }}>
              {format.label}
            </Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  ), [downloadType, handleFormatChange]);

  const actions = useMemo(() => (
    <>
      <Button
        onClick={handleDialogCancel}
        variant="outlined"
        sx={{
          color: "text.secondary",
          borderColor: "divider",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            borderColor: "text.primary",
            color: "text.primary",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={closeAndDownload}
        variant="contained"
        disabled={!downloadType}
        sx={{ fontWeight: 700, textTransform: "none" }}
      >
        Download
      </Button>
    </>
  ), [handleDialogCancel, closeAndDownload, downloadType]);

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={handleDialogCancel}
      title="Export Table"
      description="Choose a file format to download."
      maxWidth="xs"
      actions={actions}
      titleId="export-format-dialog-title"
      descriptionId="export-format-dialog-description"
      showLogo={true}
    >
      <Stack spacing={2.5}>
        {formatSelector}

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            backgroundColor: "rgba(23, 43, 77, 0.02)",
            opacity: downloadType ? 1 : 0.6,
            transition: "opacity 0.2s",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={keepSelection}
                onChange={handleToggleChange}
                disabled={!downloadType}
                color="primary"
                size="small"
                disableRipple
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 650, lineHeight: 1.2 }}>
                  Remember selection
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                  Automatically use this format next time.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0, gap: 1 }}
          />
        </Box>
      </Stack>
    </BaseDataEntryDialog>
  );
});

ExportFormatDialog.displayName = "ExportFormatDialog";

ExportFormatDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
};

export default ExportFormatDialog;
